import React, { useState, useEffect, useCallback } from 'react';
import { Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom';
import 'mapbox-gl/dist/mapbox-gl.css';
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';

// Import components
import { AuthForm } from './features/authentication/components/LoginForm';
import { ForgotPasswordPage } from './features/authentication/components/ForgotPasswordPage';
import { ResetPasswordPage } from './features/authentication/components/ResetPasswordPage';
import { VerifyEmailPage } from './features/authentication/components/VerifyEmailPage';
import { Navigation } from './components/layout/Navigation';
import { ScrollToTopOnNavigate } from './components/layout/ScrollToTopOnNavigate';
import { AppChromeHeader } from './components/layout/AppChromeHeader';
import { UnifiedMapPage } from './features/map/components/UnifiedMapPage';
import { AreaCalculatorPage } from './features/toolkit/pages/AreaCalculatorPage';
import FeedManagementPage from './features/toolkit/pages/FeedManagementPage';
import { PrintableMapPage } from './features/toolkit/pages/PrintableMapPage';
import { ClubsProGate, ToolkitProGate } from './features/pro/ProGate';
import { parseEntitlements } from './features/pro/entitlements';
import { fetchProfile, getCachedProfile, invalidateProfileCache } from './features/pro/profileCache';
import { useSkin } from './styles/SkinProvider';
import { SKIN_RETRO, SKIN_TACTICAL } from './styles/skin';
import { invalidateUserStatsCache } from './utils/statsCache';
import { invalidateBuddiesCache } from './utils/buddiesCache';
import { invalidateAssetsCache } from './utils/assetsCache';
import { invalidateImageSrcCache } from './utils/imageSrcCache';
import { invalidateClubsCache } from './features/clubs/utils/clubsCache';
import { invalidateAccountHubCache } from './features/profile/utils/accountHubCache';

// Import pages
import { ClubsPage } from './features/clubs/components/ClubsPage';
import { ClubDetailPage } from './features/clubs/components/ClubDetailPage';
import { MessagesPage } from './features/messaging/components/MessagesPage';
import HuntsPage from './features/features/components/HuntsPage';
import HuntLogPage from './features/features/components/HuntLogPage';
import ProfilePage from './features/features/components/ProfilePage';
import HuntingBuddiesPage from './features/features/components/HuntingBuddiesPage';
import { CampEssentialsPage } from './features/community/components/CampEssentialsPage';
import { CampGearFinderPage } from './features/community/components/CampGearFinderPage';
import { CampRecipesPage } from './features/community/components/CampRecipesPage';
import { SuggestStorePage } from './features/community/pages/SuggestStorePage';
import { FindAClubPage } from './features/community/pages/FindAClubPage';
import { FindAProcessorPage } from './features/community/pages/FindAProcessorPage';
import {
  COMMUNITY_DEFAULT_PATH,
  COMMUNITY_SUGGEST_STORE_PATH,
  COMMUNITY_FIND_CLUB_PATH,
  COMMUNITY_FIND_PROCESSOR_PATH,
} from './features/community/constants/communityRoutes';
import { TOOLKIT_DEFAULT_PATH, TOOLKIT_PLANTING_ASSISTANT_PATH } from './features/toolkit/constants/toolkitRoutes';
import UserProfilePage from './features/users/components/UserProfilePage';

// Import hooks and services
import { useAuth } from './features/authentication/hooks/useAuth';
import ForecastSettingsPage from './features/admin/components/ForecastSettingsPage';
import AssetAdminPage from './features/admin/components/AssetAdminPage';
// import { useUserPreferences } from './features/features/hooks/useUserPreferences';
// import { preferencesService } from './features/features/services/preferencesService';
import api from './utils/api';
import { prefetchTopPicks } from './utils/forecastCache';
import { messagingService } from './features/messaging/services/messagingService';
import { ensureMessagingIdentity } from './utils/messagingIdentity';
import { useClubBoardUnread } from './features/clubs/hooks/useClubBoardUnread';
import { useNotificationsUnread } from './features/notifications/hooks/useNotificationsUnread';
import { useInTheStandCount } from './hooks/useInTheStandCount';
import { isCampEssentialsEnabled } from './utils/featureFlags';
import { resolveCurrentPage } from './utils/resolveCurrentPage';
import { isClubMapTabLocation } from './features/clubs/utils/clubMapTabRoutes';
import NotificationsPage from './features/notifications/components/NotificationsPage';
import { useCurrentUser } from './hooks/useCurrentUser';
import { formatProfileDisplayName } from './utils/profileDisplayName';
import { isProfileHubPath } from './features/profile/profileTabs';
import { rememberProfileHubExitPath } from './features/profile/profileHubExit';
import { captureTagOutCodeFromSearch } from './features/billing/tagOutCode';

// Import context
import { MapProvider } from './features/map/context/MapContext';
import { HighlightProvider } from './features/map/context/HighlightContext';
import { ToastProvider } from './components/ui/ToastProvider';

/** Legacy Toolkit forecast URLs → Hunts Forecast tab (preserves standId). */
function HuntForecastLegacyRedirect() {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const next = new URLSearchParams();
  next.set('tab', 'forecast');
  const standId = params.get('standId');
  if (standId) next.set('standId', standId);
  return <Navigate to={`/hunts?${next.toString()}`} replace />;
}

function GatedClubDetail(props) {
  return (
    <ClubsProGate>
      <ClubDetailPage {...props} />
    </ClubsProGate>
  );
}

// Default map state
const defaultMapState = {
  center: [-98.5795, 39.8283], // Center of USA
  zoom: 4,
  pitch: 45,
  bearing: 0
};

function AppContent() {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Auth state
  const { isLoggedIn, login, signup, resendVerification, logout } = useAuth();
  
  // User preferences
  // const { preferences } = useUserPreferences();
  
  // Feature list state
  const [showFeatureList, setShowFeatureList] = useState(false);
  /** My Ground: map fills content area; chrome header hides; nav stays */
  const [myGroundMaximized, setMyGroundMaximized] = useState(false);
  /** Club Map tab: same maximize as My Ground */
  const [clubMapMaximized, setClubMapMaximized] = useState(false);

  // Map state
  const [mapState] = useState(defaultMapState);
  const [baseLayer] = useState('esri');
  const [showDebugData, setShowDebugData] = useState(false);
  // const saveTimeoutRef = useRef(null);
  // const [isMapInteracting, setIsMapInteracting] = useState(false);
  const [profilePhotoUrl, setProfilePhotoUrl] = useState('');
  const [profileDisplayName, setProfileDisplayName] = useState('Profile');
  const [hasClubsEntitlement, setHasClubsEntitlement] = useState();
  const [hasToolkitEntitlement, setHasToolkitEntitlement] = useState();
  const { currentUser } = useCurrentUser();
  const { setSkin } = useSkin();
  const [unreadTotal, setUnreadTotal] = useState(0);
  const { unreadTotal: clubsBoardUnreadTotal } = useClubBoardUnread({ enabled: isLoggedIn });
  const { unreadTotal: notificationsUnreadTotal } = useNotificationsUnread({ enabled: isLoggedIn });
  const { count: inTheStandCount } = useInTheStandCount({ enabled: isLoggedIn });
  // Determine current page from location
  const getCurrentPage = useCallback(
    () => resolveCurrentPage(location.pathname),
    [location.pathname],
  );

  const currentPage = getCurrentPage();
  console.log('Final currentPage:', currentPage);

  // Shared /profiles/me/ cache: apply instantly on route change, network only when empty.
  useEffect(() => {
    if (!isLoggedIn) {
      invalidateProfileCache();
      invalidateUserStatsCache();
      invalidateBuddiesCache();
      invalidateAssetsCache();
      invalidateImageSrcCache();
      invalidateClubsCache();
      invalidateAccountHubCache();
      setHasClubsEntitlement(undefined);
      setHasToolkitEntitlement(undefined);
      return;
    }
    let isMounted = true;
    const applyProfile = (profile) => {
      if (!isMounted || !profile) return;
      setProfilePhotoUrl(profile?.photo_url || '');
      setProfileDisplayName(formatProfileDisplayName({ profile, currentUser }));
      const parsed = parseEntitlements(profile);
      setHasClubsEntitlement(parsed.entitlements.clubs);
      setHasToolkitEntitlement(parsed.entitlements.toolkit);
      if (profile.skin === SKIN_TACTICAL || profile.skin === SKIN_RETRO) {
        setSkin(profile.skin);
      }
    };
    applyProfile(getCachedProfile({ allowStale: true }));
    (async () => {
      try {
        const res = await fetchProfile(api);
        applyProfile(res?.data);
      } catch (e) {
        // ignore
      }
    })();
    return () => { isMounted = false; };
  }, [isLoggedIn, location.pathname, currentUser, setSkin]);

  // Prefetch Hunt Forecast top-picks in background when logged in (same-day cache)
  useEffect(() => {
    if (!isLoggedIn) return;
    prefetchTopPicks(api);
  }, [isLoggedIn]);

  // Register messaging E2EE public key once per session so others can encrypt DMs to this user
  // (not only when they open the Messages page).
  useEffect(() => {
    if (!isLoggedIn) return;
    ensureMessagingIdentity(messagingService).catch((e) => {
      if (process.env.NODE_ENV === 'development') {
        console.warn('[yona] Messaging public key registration failed:', e?.response?.data || e?.message || e);
      }
    });
  }, [isLoggedIn]);

  // Poll unread total when logged in
  useEffect(() => {
    if (!isLoggedIn) {
      setUnreadTotal(0);
      return;
    }
    let cancelled = false;
    let intervalId;
    const fetchUnread = async () => {
      try {
        const data = await messagingService.getUnreadTotal();
        if (!cancelled) setUnreadTotal(Number(data?.unread_total) || 0);
      } catch (e) {
        // ignore
      }
    };
    fetchUnread();
    intervalId = setInterval(fetchUnread, 15000);
    return () => {
      cancelled = true;
      if (intervalId) clearInterval(intervalId);
    };
  }, [isLoggedIn]);

  // Must stay above auth early-returns — conditional hooks break login/logout.
  useEffect(() => {
    const isPersonalMapPage = currentPage === 'maps';
    if (!isPersonalMapPage && myGroundMaximized) {
      setMyGroundMaximized(false);
    }
  }, [currentPage, myGroundMaximized]);

  useEffect(() => {
    const onClubMapTab = isClubMapTabLocation(location.pathname, location.search);
    if (!onClubMapTab && clubMapMaximized) {
      setClubMapMaximized(false);
    }
  }, [location.pathname, location.search, clubMapMaximized]);

  const isProfileHub = isProfileHubPath(location.pathname);
  useEffect(() => {
    if (isProfileHub) {
      document.documentElement.style.setProperty('--y-app-chrome-height', '0px');
    } else {
      rememberProfileHubExitPath(location.pathname, location.search);
    }
  }, [isProfileHub, location.pathname, location.search]);

  useEffect(() => {
    const code = captureTagOutCodeFromSearch(location.search);
    if (!isLoggedIn || !code) return;
    if (location.pathname === '/profile/account') return;
    navigate('/profile/account', { replace: true });
  }, [isLoggedIn, location.pathname, location.search, navigate]);

  // Handle navigation
  const handleNavigate = useCallback((path) => {
    navigate(path);
  }, [navigate]);

  // Handle show features
  const handleShowFeatures = useCallback(() => {
    setShowFeatureList(true);
  }, []);

  // Handle close features
  const handleCloseFeatures = useCallback(() => {
    setShowFeatureList(false);
  }, []);

  const handleOpenToolkit = useCallback(() => {
    handleNavigate(TOOLKIT_DEFAULT_PATH);
  }, [handleNavigate]);

  const handleOpenCommunity = useCallback(() => {
    handleNavigate(COMMUNITY_DEFAULT_PATH);
  }, [handleNavigate]);

  // NOTE: Map state and base layer handlers were unused; removed to satisfy linter without changing behavior.

  // Check if current route is verification page (accessible without login)
  const isVerifyRoute = location.pathname.startsWith('/verify-email/');
  const isResetRoute = location.pathname.startsWith('/reset-password/');
  const isPublicAuthRoute = isVerifyRoute || isResetRoute;

  if (!isLoggedIn && !isPublicAuthRoute) {
    return (
      <Routes>
        <Route path="/verify-email/:token" element={<VerifyEmailPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
        <Route
          path="*"
          element={
            <AuthForm
              onLogin={login}
              onSignup={signup}
              onResendVerification={resendVerification}
            />
          }
        />
      </Routes>
    );
  }

  // Verify and reset links work even if a session is already in local storage.
  if (isPublicAuthRoute) {
    return (
      <div className="d-flex min-vh-100">
        <Routes>
          <Route path="/verify-email/:token" element={<VerifyEmailPage />} />
          <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
        </Routes>
      </div>
    );
  }

  const communityActive =
    currentPage === 'hunting-buddies' ||
    currentPage === 'camp-essentials' ||
    currentPage === 'suggest-store' ||
    currentPage === 'find-club' ||
    currentPage === 'find-processor';

  const campEssentialsEnabled = isCampEssentialsEnabled();
  const isClubMapFullScreen = currentPage === 'club-map';
  const isPersonalMapPage = currentPage === 'maps';
  const isClubMapTab = isClubMapTabLocation(location.pathname, location.search);
  const isMessagesPage = currentPage === 'messages';
  const isMapFullBleed = isPersonalMapPage || isClubMapFullScreen;
  const isMyGroundMaximized = isPersonalMapPage && myGroundMaximized;
  const isClubMapMaximized = isClubMapTab && clubMapMaximized;
  const isContentMapMaximized = isMyGroundMaximized || isClubMapMaximized;
  const hideAppChrome = isClubMapFullScreen || isContentMapMaximized || isProfileHub;
  const useMapFullBleedColumn = isPersonalMapPage || isClubMapMaximized;

  return (
    <div
      className={`d-flex min-vh-100${useMapFullBleedColumn ? ' app-shell--map-fullbleed' : ''}${
        isContentMapMaximized ? ' app-shell--map-maximized' : ''
      }${isPersonalMapPage && !isMyGroundMaximized ? ' app-shell--map-framed' : ''}`}
    >
      {!isClubMapFullScreen && (
        <Navigation
          currentPage={currentPage}
          pathname={location.pathname}
          search={location.search}
          onNavigate={handleNavigate}
          onLogout={logout}
          onShowFeatures={handleShowFeatures}
          showFeatureListButton={false}
          profilePhotoUrl={profilePhotoUrl}
          profileDisplayName={profileDisplayName}
          unreadMessagesTotal={unreadTotal}
          unreadClubsBoardTotal={clubsBoardUnreadTotal}
          unreadNotificationsTotal={notificationsUnreadTotal}
          onOpenToolkit={handleOpenToolkit}
          onOpenCommunity={handleOpenCommunity}
          communityActive={communityActive}
          overlayNav={isPersonalMapPage || isClubMapMaximized}
          hasClubsEntitlement={hasClubsEntitlement}
          hasToolkitEntitlement={hasToolkitEntitlement}
        />
      )}

      <div
        className={
          useMapFullBleedColumn
            ? 'app-main-map-fullbleed d-flex flex-column'
            : 'flex-grow-1 d-flex flex-column position-relative'
        }
        style={useMapFullBleedColumn ? undefined : { zIndex: 0 }}
      >
        {!hideAppChrome && (
          <AppChromeHeader
            currentPage={currentPage}
            pathname={location.pathname}
            search={location.search}
            profilePhotoUrl={profilePhotoUrl}
            profileDisplayName={profileDisplayName}
            unreadNotificationsTotal={notificationsUnreadTotal}
            onStandCount={inTheStandCount}
            onNavigate={handleNavigate}
          />
        )}
        <Routes>
          {/* My Ground is the default landing destination */}
          <Route path="/" element={<Navigate to="/maps" replace />} />
          <Route path="/home" element={<Navigate to="/maps" replace />} />
          {/* Dedicated Hunt Forecast page — lives under Hunts; keep legacy toolkit URL */}
          <Route path="/toolkit/hunt-forecast" element={<HuntForecastLegacyRedirect />} />
          <Route path="/hunt-forecast" element={<HuntForecastLegacyRedirect />} />
          {/* Admin - Forecast Settings (admin-only, server-enforced) */}
          <Route path="/admin/forecast-settings" element={<ForecastSettingsPage />} />
          {/* Admin - Asset Management (admin-only, server-enforced) */}
          <Route path="/admin/assets" element={<AssetAdminPage />} />
          
          {/* Unified Map Routes - explicitly for maps */}
          <Route path="/maps" element={
            <UnifiedMapPage 
              showFeatureList={showFeatureList}
              onCloseFeatureList={handleCloseFeatures}
              onShowFeatureList={handleShowFeatures}
              mapMaximized={myGroundMaximized}
              onToggleMapMaximize={() => setMyGroundMaximized((prev) => !prev)}
            />
          } />
          <Route path="/clubs/:clubId/map" element={
            <GatedClubDetail
              showFeatureList={showFeatureList}
              onCloseFeatureList={handleCloseFeatures}
              onShowFeatureList={handleShowFeatures}
              mapMaximized={isClubMapMaximized}
              onToggleMapMaximize={() => setClubMapMaximized((prev) => !prev)}
            />
          } />
          
          {/* Other routes remain the same */}
          <Route
            path="/clubs"
            element={(
              <ClubsProGate>
                <ClubsPage />
              </ClubsProGate>
            )}
          />
          <Route path="/clubs/:clubId" element={
            <GatedClubDetail
              showFeatureList={showFeatureList}
              onCloseFeatureList={handleCloseFeatures}
              onShowFeatureList={handleShowFeatures}
              mapMaximized={isClubMapMaximized}
              onToggleMapMaximize={() => setClubMapMaximized((prev) => !prev)}
            />
          } />
          <Route path="/clubs/:clubId/rules" element={
            <GatedClubDetail
              showFeatureList={showFeatureList}
              onCloseFeatureList={handleCloseFeatures}
              onShowFeatureList={handleShowFeatures}
            />
          } />
          <Route path="/clubs/:clubId/calendar" element={
            <GatedClubDetail
              showFeatureList={showFeatureList}
              onCloseFeatureList={handleCloseFeatures}
              onShowFeatureList={handleShowFeatures}
            />
          } />
          <Route path="/clubs/:clubId/board" element={
            <GatedClubDetail
              showFeatureList={showFeatureList}
              onCloseFeatureList={handleCloseFeatures}
              onShowFeatureList={handleShowFeatures}
            />
          } />
          <Route path="/clubs/:clubId/board/:threadId" element={
            <GatedClubDetail
              showFeatureList={showFeatureList}
              onCloseFeatureList={handleCloseFeatures}
              onShowFeatureList={handleShowFeatures}
            />
          } />
          <Route path="/messages" element={<MessagesPage />} />
          <Route path="/messages/new" element={<MessagesPage />} />
          <Route path="/messages/:conversationId" element={<MessagesPage />} />
          <Route path="/notifications" element={<NotificationsPage />} />
          <Route path="/toolkit" element={<Navigate to={TOOLKIT_DEFAULT_PATH} replace />} />
          <Route
            path="/toolkit/planting-assistant/*"
            element={(
              <ToolkitProGate>
                <AreaCalculatorPage />
              </ToolkitProGate>
            )}
          />
          <Route
            path="/toolkit/area-calculator"
            element={<Navigate to={TOOLKIT_PLANTING_ASSISTANT_PATH} replace />}
          />
          <Route
            path="/toolkit/feed-management"
            element={(
              <ToolkitProGate>
                <FeedManagementPage />
              </ToolkitProGate>
            )}
          />
          <Route
            path="/toolkit/printable-map"
            element={(
              <ToolkitProGate>
                <PrintableMapPage />
              </ToolkitProGate>
            )}
          />
          <Route path="/hunts" element={<HuntsPage />} />
          <Route path="/hunts/log" element={<HuntLogPage />} />
          <Route path="/hunts/edit/:huntId" element={<HuntLogPage />} />
          <Route path="/profile/account" element={<ProfilePage />} />
          <Route path="/profile/my-stuff" element={<ProfilePage />} />
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/hunting-buddies" element={<HuntingBuddiesPage />} />
          <Route path="/community" element={<Navigate to={COMMUNITY_DEFAULT_PATH} replace />} />
          <Route path={COMMUNITY_SUGGEST_STORE_PATH} element={<SuggestStorePage />} />
          <Route path={COMMUNITY_FIND_CLUB_PATH} element={<FindAClubPage />} />
          <Route path={COMMUNITY_FIND_PROCESSOR_PATH} element={<FindAProcessorPage />} />
          <Route
            path="/camp-essentials"
            element={
              campEssentialsEnabled ? <CampEssentialsPage /> : <Navigate to="/" replace />
            }
          />
          <Route
            path="/camp-essentials/gear/*"
            element={
              campEssentialsEnabled ? <CampGearFinderPage /> : <Navigate to="/" replace />
            }
          />
          <Route
            path="/camp-essentials/recipes/*"
            element={
              campEssentialsEnabled ? <CampRecipesPage /> : <Navigate to="/" replace />
            }
          />
          <Route path="/users/:userId" element={<UserProfilePage />} />
        </Routes>

        {/* Mobile bottom spacer to ensure content clears fixed bottom nav (hidden on full-screen club map and messages) */}
        {!isMapFullBleed && !isMessagesPage && (
          <div
            className="d-md-none app-mobile-content-clearance"
            style={{ height: 'var(--y-mobile-content-clearance, 104px)' }}
            aria-hidden="true"
          />
        )}

        {/* Debug Panel - only show on personal maps */}
        {showDebugData && currentPage === 'map' && (
          <div className="debug-panel">
            <h4>Debug Data</h4>
            <pre>{JSON.stringify({ mapState, baseLayer }, null, 2)}</pre>
            <button onClick={() => setShowDebugData(false)}>Hide Debug</button>
          </div>
        )}
      </div>
    </div>
  );
}

function App() {
  return (
    <MapProvider>
      <HighlightProvider>
        <ToastProvider>
          <ScrollToTopOnNavigate />
          <AppContent />
        </ToastProvider>
      </HighlightProvider>
    </MapProvider>
  );
}

export default App;